import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { format } from "date-fns";
import { toast } from "sonner";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { addCompanyHeader } from "@/lib/pdfHeader";
import { openSafePdfPopup } from "@/lib/safePopup";
import { formatCurrency, COST_TYPES, deriveCostType, calcLineItemTotals, calcScopeCost, summarizeProposalDisplay } from "./proposalUtils";

export function generateProposalPDF(form, clients, sites, totals) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = addCompanyHeader(doc, 10);

  const client = clients.find(c => c.id === form.client_id);
  const site = sites?.find(s => s.id === form.site_id);
  // Site defaults to "Main" if no linked site; address falls back through
  // explicit project_address → linked site address → client billing addr.
  const siteName = site?.name || "Main";
  const buildAddr = (obj) => obj ? [obj.address, obj.city, obj.state, obj.zip_code].filter(Boolean).join(", ") : "";
  const siteAddr = form.project_address || buildAddr(site) || buildAddr(client) || "";
  const clientName = client?.name || "Customer";

  // --- Header rendered by addCompanyHeader (logo + company info + separator) ---

  // PROJECT / BILL TO layout — swapped per UX request. Left column:
  // PROJECT → ADDRESS → CONTACT → ACCOUNT MGR. Right column: BILL TO →
  // SITE → PROPOSAL DATE.
  const rightX = pageWidth - margin;
  const proposalDate = form.proposal_date ? format(new Date(form.proposal_date + "T00:00:00"), "MM/dd/yyyy") : format(new Date(), "MM/dd/yyyy");
  const contactName = form.site_contact_name || form.contact_name || "";
  const contactEmail = form.site_contact_email || form.contact_email || "";
  const contactLine = contactName && contactEmail ? `${contactName} · ${contactEmail}` : (contactName || contactEmail || "—");
  // Row 1 — PROJECT / CUSTOMER
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("PROJECT", margin, y);
  doc.text("CUSTOMER", rightX, y, { align: "right" });
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
  doc.text(form.name || "—", margin, y + 5);
  doc.text(clientName || "—", rightX, y + 5, { align: "right" });
  y += 12;
  // Row 2 — SITE / ADDRESS — site defaults to "Main" and address always
  // resolves (see siteName / siteAddr fallbacks above).
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("SITE", margin, y);
  doc.text("ADDRESS", rightX, y, { align: "right" });
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
  doc.text(siteName, margin, y + 5);
  doc.text(siteAddr || "—", rightX, y + 5, { align: "right", maxWidth: 90 });
  y += 12;
  // Row 3 — CONTACT (name + phone) / PROPOSAL DATE
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("CONTACT", margin, y);
  doc.text("PROPOSAL DATE", rightX, y, { align: "right" });
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
  doc.text(contactLine, margin, y + 5, { maxWidth: 90 });
  doc.text(proposalDate, rightX, y + 5, { align: "right" });
  y += 12;
  // Row 4 — PREPARED BY (left only, includes name + email)
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("PREPARED BY", margin, y);
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
  doc.text(`${form.prepared_by || "—"}${form.prepared_by_email ? ` · ${form.prepared_by_email}` : ""}`, margin, y + 5);
  doc.setTextColor(0, 0, 0);
  y += 12;

  // Qualifications
  const quals = form.qualifications.filter(q => q.trim());
  if (quals.length > 0) {
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, "bold");
    doc.text("Qualifications", margin, y);
    doc.setFont(undefined, "normal");
    y += 5;
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    quals.forEach(q => {
      const lines = doc.splitTextToSize(`\u2022  ${q}`, pageWidth - 2 * margin);
      doc.text(lines, margin + 2, y);
      y += lines.length * 3.5;
    });
    y += 4;
  }

  // Terms (after qualifications, before scopes)
  const termItems = Array.isArray(form.terms) ? form.terms.filter(t => t.trim()) : [];
  if (termItems.length > 0) {
    if (y > 250) { doc.addPage(); y = 15; }
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, "bold");
    doc.text("Terms", margin, y);
    doc.setFont(undefined, "normal");
    y += 5;
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    termItems.forEach(t => {
      const lines = doc.splitTextToSize(`\u2022  ${t}`, pageWidth - 2 * margin);
      doc.text(lines, margin + 2, y);
      y += lines.length * 3.5;
    });
    y += 4;
  }

  // Scope tables — CC fee silently distributed into material so the
  // displayed per-row Material column + scope footer sums match the grand
  // total (which already includes the fee via calcProposalTotals).
  // T&M proposals skip the priced tables + totals entirely.
  const isTM = form.project_type === "tm";
  if (isTM) {
    doc.setFontSize(10); doc.setTextColor(30, 30, 30); doc.setFont(undefined, "bold");
    doc.text("Time & Materials", margin, y);
    doc.setFont(undefined, "normal");
    y += 5;
    doc.setFontSize(8); doc.setTextColor(60, 60, 60);
    const tmLines = doc.splitTextToSize(
      "This project will be performed and billed on a Time & Materials basis. Labor, materials, freight, tax, overtime, and applicable markups are billed as incurred per the qualifications above.",
      pageWidth - 2 * margin
    );
    doc.text(tmLines, margin, y);
    y += tmLines.length * 3.5 + 6;
  }
  const feePct = form.cc_fee_pct == null ? 0 : form.cc_fee_pct;
  // iter244 — add-ons (drive time / equipment / travel / disposal) are
  // aggregated across scopes and rendered in the totals summary, not
  // inside the scope tables (keeps flat-rate line prices intact).
  const disp = isTM ? null : summarizeProposalDisplay(form.scopes || [], feePct, form.hide_tax ? (totals.taxAmount || 0) : 0);
  const ccScopes = isTM ? [] : disp.ccScopes;
  ccScopes.forEach(scope => {
    if (y > 250) { doc.addPage(); y = 15; }

    // Scope header
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.setFont(undefined, "bold");
    doc.text(scope.name || "Scope", margin, y);
    doc.setFont(undefined, "normal");
    y += 2;

    // #159 (Group H cleanup): scopeLineTotal was previously accumulated
    // here for an old scope-footer row that has been removed. The grand
    // totals at the bottom are the single source of truth now.
    const breakout = !!form.show_breakdown;
    const tableData = (scope._lines || []).map(item => {
      const matBilled = item._displayMaterial || 0;
      const labBilled = item._displayLabor || 0;
      const frtBilled = item._displayFreight || 0;
      const lineTotal = item._displayTotal || 0;
      // Combine name + description into a single cell with a newline so
      // autoTable's word-wrap handles multi-line descriptions and the row
      // height auto-grows to fit. Falls back to whichever field is set.
      const baseName = item.name || item.description || "";
      const subDesc = (item.name && item.description && item.description !== item.name) ? item.description : "";
      const nameCell = subDesc ? `${baseName}\n${subDesc}` : baseName;
      if (breakout) {
        return [item.part_number || "—", nameCell, `${item.qty}`, formatCurrency(matBilled), formatCurrency(frtBilled), formatCurrency(labBilled), formatCurrency(lineTotal)];
      }
      return [item.part_number || "—", nameCell, `${item.qty}`, formatCurrency(lineTotal)];
    });

    // iter244 — Travel / Equipment / Disposal / Drive Time no longer render
    // inside the scope tables; they are aggregated across scopes and shown
    // as their own lines in the totals summary below.

    autoTable(doc, {
      startY: y,
      head: [breakout
        ? ["Part #", "Name", "Qty", "Material", "Freight", "Labor", "Total"]
        : ["Part #", "Name", "Qty", "Total"]],
      body: tableData,
      // No scope-subtotal footer row — the grand Total block below is the
      // source of truth. Redundant line removed per UX request.
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 1.5, textColor: [51, 65, 85] },
      headStyles: { fillColor: [51, 65, 85], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: breakout
        ? { 0: { cellWidth: 20 }, 1: { cellWidth: 52 }, 2: { cellWidth: 10, halign: "center" }, 3: { cellWidth: 20, halign: "right" }, 4: { cellWidth: 19, halign: "right" }, 5: { cellWidth: 19, halign: "right" }, 6: { cellWidth: 20, halign: "right" } }
        : { 0: { cellWidth: 25 }, 1: { cellWidth: 80 }, 2: { cellWidth: 15, halign: "center" }, 3: { cellWidth: 25, halign: "right" } },
      theme: "striped",
    });
    y = doc.lastAutoTable.finalY + 6;
  });

  // Summary — no horizontal rule above (per UX request).
  if (!isTM) {
  if (y > 240) { doc.addPage(); y = 15; }
  y += 6;

  const summaryX = pageWidth - margin - 70;
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);

  // iter244 — Subtotal shows scope line items only; add-ons (aggregated
  // across scopes) get their own lines, then Tax, then Total. Grand total
  // math is unchanged.
  const summaryLines = [
    ["Subtotal:", formatCurrency(disp?.itemsSubtotal || 0)],
  ];
  if ((disp?.driveTime || 0) > 0) summaryLines.push(["Drive Time:", formatCurrency(disp.driveTime)]);
  if ((disp?.equipment || 0) > 0) summaryLines.push(["Equipment:", formatCurrency(disp.equipment)]);
  if ((disp?.travel || 0) > 0) summaryLines.push(["Travel:", formatCurrency(disp.travel)]);
  if ((disp?.disposal || 0) > 0) summaryLines.push(["Disposal:", formatCurrency(disp.disposal)]);
  // iter257 — Discount line (only when non-zero).
  if ((totals.discountAmount || 0) > 0) {
    const discLabel = (totals.discountType || form.discount_type) === "pct"
      ? `Discount (${parseFloat(form.discount_value) || 0}%):`
      : "Discount:";
    summaryLines.push([discLabel, `-${formatCurrency(totals.discountAmount)}`]);
  }
  // iter310 — a hide-tax proposal prints no tax line; the tax is already
  // inside the Material figures above.
  if (!form.hide_tax) {
    summaryLines.push([`Tax (${parseFloat(form.tax_rate) || 0}%):`, formatCurrency(totals.taxAmount)]);
  }
  summaryLines.forEach(([label, val]) => {
    doc.text(label, summaryX, y);
    doc.text(val, pageWidth - margin, y, { align: "right" });
    y += 5;
  });
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.setFont(undefined, "bold");
  doc.text("Total:", summaryX, y + 2);
  doc.text(formatCurrency(totals.total), pageWidth - margin, y + 2, { align: "right" });
  doc.setFont(undefined, "normal");
  y += 14;
  }

  // Acceptance (iter305) — five signed lines: signature, printed name,
  // title, date accepted, PO #. "Bid Date" is gone from here; the
  // proposal date prints in the header.
  if (y > 218) { doc.addPage(); y = 15; }
  y += 14;
  const accW = pageWidth - 2 * margin;
  const leftW = accW * 0.56;
  const rightStart = margin + accW * 0.62;
  const acceptanceRow = (leftLabel, rightLabel, leftValue) => {
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(leftLabel, margin, y);
    if (rightLabel) doc.text(rightLabel, rightStart, y);
    if (leftValue) {
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(leftValue, margin, y + 6);
    }
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 8, margin + leftW, y + 8);
    if (rightLabel) doc.line(rightStart, y + 8, pageWidth - margin, y + 8);
    y += 16;
  };
  acceptanceRow("Accepted By (signature)", "Date Accepted");
  acceptanceRow("Printed Name", "Title");
  acceptanceRow("PO # (if applicable)", "", (form.customer_po_number || "").trim());

  // Save — three-tier strategy so the user ALWAYS gets a path to save locally:
  //   (1) File System Access API → real OS "Save As" dialog (Chrome/Edge/Brave ≥ 86).
  //   (2) Anchor download with blob URL → standard browser download manager
  //       (works in Firefox/Safari and in Chromium when iframe allows it).
  //   (3) Open PDF in a new tab as a data URL so the user can hit Cmd+S /
  //       Ctrl+S to save wherever they want. This is the most bulletproof
  //       fallback because data: URLs bypass Brave Shields' blob blocking and
  //       don't need `allow="downloads"` in an iframe.
  const fileName = `${form.name || "Proposal"} - ${format(new Date(), "MM.dd.yy")}.pdf`;

  (async () => {
    const blob = doc.output("blob");

    // (1) Native OS save picker
    if (typeof window.showSaveFilePicker === "function") {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{ description: "PDF Document", accept: { "application/pdf": [".pdf"] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(`Saved: ${handle.name}`);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled
        // fall through for SecurityError / NotAllowedError
      }
    }

    // (2) Anchor download
    let anchorWorked = false;
    try {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      a.rel = "noopener";
      a.style.position = "fixed";
      a.style.top = "-100px";
      document.body.appendChild(a);
      a.click();
      anchorWorked = true;
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) {}
        URL.revokeObjectURL(objectUrl);
      }, 4000);
    } catch (_) { /* falls through to #3 */ }

    // (3) Open the PDF in a new tab so the user can Save As manually. We do
    // this regardless of whether (2) "worked" because in sandboxed iframes
    // (Emergent preview / Brave Shields) the anchor click often silently
    // fails even though no exception is thrown. This path uses jsPDF's
    // data URL output which is reliably allowed by browsers and ad blockers.
    setTimeout(() => {
      try {
        const dataUri = doc.output("datauristring");
        // Uses the centralized `openSafePdfPopup` helper so the
        // user-controlled `fileName` is never interpolated into an
        // HTML string (iter170 XSS hardening). Returns false when the
        // popup is blocked.
        const opened = openSafePdfPopup({
          title: fileName,
          src: dataUri,
          background: "#ffffff",
        });
        if (opened) {
          toast.success(anchorWorked
            ? `${fileName} — also opened in new tab; press Cmd/Ctrl+S to save elsewhere.`
            : `${fileName} opened in new tab. Press Cmd/Ctrl+S to save.`);
        } else if (!anchorWorked) {
          toast.error("Pop-up blocked. Allow pop-ups for this site, or turn off Brave Shields, then try again.");
        } else {
          toast.success(`PDF downloaded: ${fileName}`);
        }
      } catch (err) {
        if (anchorWorked) {
          toast.success(`PDF downloaded: ${fileName}`);
        } else {
          toast.error(`Download blocked. Turn off Brave Shields for this site and retry. (${err?.message || "unknown"})`);
        }
      }
    }, 100);
  })();
}

// ============== PIPELINE ROW COMPONENT ==============

